/* eslint-disable max-lines-per-function */
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { apiCall } from "../../config/api";
import { LoadingDots } from "../../shared/components/Loading";
import { Button } from "../../shared/components/ui/button";
import { useTransitionNavigation } from "../../shared/hooks/useTransitionNavigation";
import { pageTransitions } from "../../shared/transitions/pageTransitions";

/**
 * Step 3: Complete user and save preferences
 */
export function CompleteStep(): ReactElement {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { transitionTo } = useTransitionNavigation();

  const savePreferences = async (): Promise<void> => {
    setSaving(true);
    setError(null);

    try {
      // Get stored user data
      const displayName = localStorage.getItem("user-displayName") || "";
      const genresJson = localStorage.getItem("user-genres");
      const languagesJson = localStorage.getItem("user-languages");

      const genres = genresJson ? (JSON.parse(genresJson) as string[]) : [];
      const languages = languagesJson ? (JSON.parse(languagesJson) as string[]) : [];

      // Save to backend
      const response = await apiCall("/v1/user/onboarding", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          displayName: displayName || null,
          preferences: {
            genres,
            languages,
            subtitlesEnabled: true, // Default to enabled
          },
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save preferences");
      }

      // Clear user data from localStorage
      localStorage.removeItem("user-displayName");
      localStorage.removeItem("user-genres");
      localStorage.removeItem("user-languages");

      // Redirect to home after short delay
      setTimeout(() => {
        transitionTo("/home", pageTransitions.scaleDown);
      }, 1500);
    } catch (err) {
      console.error("Failed to save user preferences:", err);
      setError(err instanceof Error ? err.message : "Failed to save preferences");
      setSaving(false);
    }
  };

  // Auto-save on mount
  useEffect(() => {
    void savePreferences();
  }, []);

  return (
    <motion.div
      className="max-w-2xl mx-auto"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="text-center mb-12">
        {!error && !saving && (
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 border-2 border-green-500 mb-6">
            <Check size={40} className="text-green-500" />
          </div>
        )}

        <h1 className="text-4xl font-bold text-white mb-4">
          {saving && "Setting up your account..."}
          {!saving && error && "Oops!"}
          {!saving && !error && "All set! 🎉"}
        </h1>

        {saving && <p className="text-lg text-neutral-400">We're personalizing your experience</p>}

        {error && <p className="text-lg text-red-400 mb-4">{error}</p>}

        {!saving && !error && <p className="text-lg text-neutral-400">Your account is ready. Let's start watching!</p>}
      </div>

      <div className="bg-black/50 backdrop-blur-md rounded-lg border border-neutral-800/60 p-8">
        {saving && (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-12 h-12 border-2 border-lumi-accent border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-neutral-400 flex items-center">
              Saving your preferences
              <LoadingDots className="ml-1" />
            </p>
          </div>
        )}
        {!saving && error && (
          <div className="space-y-4">
            <p className="text-neutral-400 text-center">Something went wrong. Please try again.</p>
            <div className="flex gap-3">
              <Button onClick={() => void savePreferences()} variant="outline" className="flex-1">
                Skip for now
              </Button>
              <Button onClick={() => void savePreferences()} className="flex-1">
                Retry
              </Button>
            </div>
          </div>
        )}
        {!saving && !error && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-lumi-accent mb-1">30</div>
                <div className="text-xs text-neutral-500">Days Free Trial</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-lumi-accent mb-1">∞</div>
                <div className="text-xs text-neutral-500">Titles to Watch</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-lumi-accent mb-1">4K</div>
                <div className="text-xs text-neutral-500">Quality Streaming</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Progress indicator */}
      <div className="flex justify-center gap-2 mt-8">
        <div className="w-8 h-1 bg-lumi-accent rounded-full" />
        <div className="w-8 h-1 bg-lumi-accent rounded-full" />
        <div className="w-8 h-1 bg-lumi-accent rounded-full" />
      </div>
      <p className="text-center text-xs text-neutral-500 mt-3">Step 3 of 3</p>
    </motion.div>
  );
}
